import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { UserCheck } from 'lucide-react';
import { ProtectedRoute } from '@/components/auth/protected-route';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { useApproveUser, usePendingUsers, useRejectUser } from '@/hooks/use-users';
import { usersApi } from '@/services/api';

export const Route = createFileRoute('/users')({
  component: () => (
    <ProtectedRoute requireAdmin>
      <UsersPage />
    </ProtectedRoute>
  ),
});

function UsersPage() {
  const { data: pending, isLoading: pendingLoading } = usePendingUsers();
  const approve = useApproveUser();
  const reject = useRejectUser();

  const { data: approvedData } = useQuery({
    queryKey: ['users', 'all'],
    queryFn: usersApi.getUsers,
  });

  const approvedHeroes = Object.values(approvedData?.users ?? {}).filter((u) => u.status === 'approved');

  return (
    <div className="space-y-8">
      <h1 className="inline-block border-b-4 border-primary pb-1 text-4xl tracking-wide">Heroes</h1>

      <section className="space-y-4">
        <h2 className="inline-block border-b-4 border-accent pb-1 text-3xl tracking-wide">Pending requests</h2>
        {pendingLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : !pending || pending.length === 0 ? (
          <EmptyState
            icon={UserCheck}
            title="No pending requests"
            description="There are no hero requests waiting for a decision."
          />
        ) : (
          <div className="space-y-3">
            {pending.map((user) => (
              <Card key={user.id}>
                <CardHeader>
                  <CardTitle className="text-base">{user.name || user.email || user.id}</CardTitle>
                  {user.email && <p className="text-sm text-muted-foreground">{user.email}</p>}
                </CardHeader>
                <CardContent className="space-y-3">
                  {user.note && (
                    <div className="comic-bubble p-4 text-sm">
                      <p className="font-medium">How they know Chris:</p>
                      <p className="mt-1 whitespace-pre-wrap text-muted-foreground">{user.note}</p>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => approve.mutate(user.id)}
                      disabled={approve.isPending || reject.isPending}
                    >
                      Approve hero
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => reject.mutate(user.id)}
                      disabled={approve.isPending || reject.isPending}
                    >
                      Decline
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </section>

      <section className="space-y-4">
        <h2 className="inline-block border-b-4 border-secondary pb-1 text-3xl tracking-wide">Approved heroes</h2>
        {approvedHeroes.length === 0 ? (
          <p className="text-sm text-muted-foreground">No approved heroes yet.</p>
        ) : (
          <div className="space-y-2">
            {approvedHeroes.map((user) => (
              <Card key={user.id}>
                <CardContent className="py-3">
                  <p className="font-medium">{user.name || user.email || user.id}</p>
                  {user.email && user.name && <p className="text-sm text-muted-foreground">{user.email}</p>}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
